<template>
    <div class="modal" dusk="modal" :class="getModalClass" @click="handleClickOffModal" ref="modal" >
        <div class="modal__content">

            <ul class="modal__pagination" v-if="hasPagination">
                <li class="modal__pagination__button modal__pagination__button--previous" v-if="hasPreviousButton">
                    <a href="#previous" @click.prevent.stop="goToSlide('previous')">
                        prev
                        <span class="show-for-sr">previous</span>
                    </a>
                </li>
                <li class="modal__pagination__button modal__pagination__button--next" v-if="hasNextButton">
                    <a href="#next" @click.prevent.stop="goToSlide('next')">
                        next
                        <span class="show-for-sr">next</span>
                    </a>
                </li>
            </ul>

            <div class="box box--white" ref="modalElement" @click.stop>

                <slot></slot>

            </div>
            <div class="modal__close" dusk="close-modal-button" v-if="showCloseButton" @click.prevent.stop="handleCloseModal"><span class="sr-only">Close Modal</span></div>
        </div>
    </div>
</template>

<script setup>
    import {ref, onMounted, onBeforeUnmount, computed, watch, nextTick} from 'vue'

    const props = defineProps({
        modalWidth              : { default: 'normal' },
        hasPagination           : { type: Boolean, default: false },
        hasPreviousButton       : { type: Boolean, default: true },
        hasNextButton           : { type: Boolean, default: true },
        showModal               : { type: Boolean, default: false },
        showCloseButton         : { type: Boolean, default: true },
        clickOffToClose         : { type: Boolean, default: true },
        escapeToCloseProp       : { type: Boolean, default: null },  // will default to clickOffToClose if null prop
        fitVertically           : { type: Boolean, default: false },
        expandForDraggedFile    : { type: Boolean, default: false },
        isNestedModal           : { type: Boolean, default: false }, 
    })

    const emits = defineEmits(['goToSlide', 'closeModal'])

    const modal = ref(null)
    const modalOpen = ref(false)
    const escapeToClose = ref(false)
    const dragOver = ref(false)
    const dragChildCounter = ref(0)
    const htmlBody = ref(null)


    const getModalClass = computed(() => {
        let classList = []
        switch(props.modalWidth){
            case 'normal':
            case 'medium':
                break;
            case 'wide':
            case 'large':
                classList.push('modal--wide')
                break;
            case 'narrow':
            case 'small':
                classList.push('modal--narrow')
                break;
            case 'full-width':
                classList.push('modal--full-width')
                let isMobile = document.getElementById('force-mobile-nav').checked
                if(isMobile) { classList.push('modal--full-width--mobile') }
                break;
            default:
                console.error("ERROR: Invalid modalWidth prop!")
        }
        if(modalOpen.value) {
            classList.push('modal--open')
        }
        if(props.hasPagination) {
            classList.push('modal--has-pagination')
        }
        if(props.fitVertically) {
            classList.push('modal--fit-vertically')
        }
        if(dragOver.value) {
            classList.push('modal--expand-for-file')
        }
        return classList
    })

    watch(
        () => props.showModal,
        (newValue) => detectShowState()
    )

    onMounted(() => {
        htmlBody.value = document.body
        getEscCloseValue();
        setupModal()
        detectShowState()
        modal.value.focus()

    })

    onBeforeUnmount(() => {
        modal.value.remove();
        if(!props.isNestedModal) {
            htmlBody.value.classList.remove('modal-is-open');
        }
    })

    function goToSlide(direction) {
        let data = {direction: direction}
        emits('goToSlide', data);
    }

    async function handleLockBodyScroll() {
        if(!props.isNestedModal) {
            await nextTick(()=>{
                if(props.showModal) {
                    htmlBody.value.classList.add('modal-is-open');
                } else if(!props.showModal) {
                    htmlBody.value.classList.remove('modal-is-open');
                }
            })
        }
    }

    function handleCloseModal(){
        if(!props.isNestedModal) {
            htmlBody.value.classList.remove('modal-is-open');
        }
        emits("closeModal")
        document.removeEventListener('keydown', handleKeyPress)
    }

    function handleClickOffModal() {
        if(props.clickOffToClose === true) {
            handleCloseModal();
        }
    }
    function getEscCloseValue(){
        escapeToClose.value = props.escapeToCloseProp !== null ? props.escapeToCloseProp : props.clickOffToClose;
    }
    function detectShowState(){
        setTimeout(() => {
            modalOpen.value = props.showModal;
            handleKeyboardListeners()
            handleLockBodyScroll();
        }, 20);
    }
    function setupModal(){
        moveModalToEndOfDom()
    }
    async function moveModalToEndOfDom(){
        await nextTick(()=>{
            if(modal.value && modal.value.parentNode) {
                modal.value.parentNode.removeChild(modal.value);
            }
            
            if(modal.value){
                document.getElementsByTagName('body')[0].appendChild(modal.value);
            }
        })
    }

    function handleKeyPress(e){
        if(e.key == 'Escape') {
            handleCloseModal()
        }
    }

    function handleKeyboardListeners(){
        if(props.showModal && escapeToClose.value === true) {
            document.addEventListener("keydown", handleKeyPress);
        } else {
            document.removeEventListener('keydown', handleKeyPress)
        }
    }

</script>
